export const PAYMENT_LABELS = { boleto: "Boleto", carne: "Carnê" };
export const CONTRACT_SIGNED_LABELS = { true: "Assinado", false: "Não assinado" };
export const IMAGE_AUTHORIZATION_LABELS = { sim: "Autorizado", nao: "Não autorizado" };

function scalar(value) {
  if (Array.isArray(value)) return scalar(value[0]);
  if (value && typeof value === "object" && "_" in value) return scalar(value._);
  return value == null ? "" : String(value);
}

function firstPresent(...values) {
  return values.find((value) => value !== undefined && value !== null);
}

function normalizeStatusBoolean(value) {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const normalized = scalar(value).toLowerCase();
  if (["true", "1", "sim", "assinado"].includes(normalized)) return true;
  if (["false", "0", "nao", "não", "nao assinado", "não assinado"].includes(normalized)) return false;
  return null;
}

function normalizeStatus(status = {}) {
  return {
    id: firstPresent(status.id, status.ID),
    paymentOption: scalar(firstPresent(status.paymentOption, status.payment_option, status.formaPagamento)).toLowerCase(),
    contractSigned: normalizeStatusBoolean(firstPresent(status.contractSigned, status.contract_signed, status.contratoAssinado)),
    imageAuthorization: scalar(firstPresent(status.imageAuthorization, status.image_authorization, status.autorizaDireitoImagem)).toLowerCase(),
    imageRestrictions: scalar(firstPresent(status.imageRestrictions, status.image_restrictions, status.restricoesDireitoImagem)),
  };
}

export function readEnrollmentPayment(student) {
  const raw =
    student?.Observacao ||
    student?.ObservacaoAluno ||
    student?.Observacoes ||
    student?.Observações ||
    student?.ObservacoesAluno ||
    student?.ObservaçõesAluno ||
    student?.observacao ||
    student?.observacoes ||
    "";
  const text = scalar(raw);
  const match = text.match(/\*{3} FORMA DE PAGAMENTO \*{3}\s*(BOLETO|CARN[EÊ])(?=\s*(?:\*{3}|$))/i);
  if (!match) return "";
  return match[1].normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

export function paymentSummary(value) {
  return PAYMENT_LABELS[value] ? `*** FORMA DE PAGAMENTO *** ${value.toUpperCase()}` : "";
}

export function readImageAuthorization(student) {
  const raw =
    student?.Observacao ||
    student?.ObservacaoAluno ||
    student?.Observacoes ||
    student?.Observações ||
    student?.ObservacoesAluno ||
    student?.ObservaçõesAluno ||
    student?.observacao ||
    student?.observacoes ||
    "";
  const text = scalar(raw);
  const match = text.match(/\*{3} DIREITO DE IMAGEM \*{3}\s*(AUTORIZADO|NAO AUTORIZADO|NÃO AUTORIZADO)(?=\s*(?:\*{3}|$))/i);
  if (!match) return "";
  return /NA[OÃ] AUTORIZADO/i.test(match[1]) ? "nao" : "sim";
}

export function readImageRestrictions(student) {
  const raw =
    student?.Observacao ||
    student?.ObservacaoAluno ||
    student?.Observacoes ||
    student?.Observações ||
    student?.ObservacoesAluno ||
    student?.ObservaçõesAluno ||
    student?.observacao ||
    student?.observacoes ||
    "";
  const text = scalar(raw);
  const match = text.match(/\*{3} RESTRICOES DIREITO DE IMAGEM \*{3}\s*(.*?)(?=\s*\*{3}|$)/i);
  return match ? match[1].trim() : "";
}

export function replaceEnrollmentPaymentSummary(observation, value) {
  const nextSummary = paymentSummary(value);
  const current = scalar(observation);
  const withoutPayment = current
    .replace(/\s*\*{3} FORMA DE PAGAMENTO \*{3}\s*(?:BOLETO|CARN[EÊ])(?=\s*(?:\*{3}|$))/i, "")
    .trim();
  return [withoutPayment, nextSummary].filter(Boolean).join(" ");
}

export function paymentReportRows(enrollments, students) {
  return paymentReportRowsWithStatuses(enrollments, students, {});
}

export function paymentReportRowsWithStatuses(enrollments, students, statusesByStudentId = {}) {
  const seen = new Set();
  return (enrollments || []).flatMap((enrollment) => {
    const id = scalar(enrollment.AlunoID || enrollment.AlunoId || enrollment.AlunoIDMatricula || enrollment.AlunoIdSponte);
    if (!id || seen.has(String(id))) return [];
    seen.add(String(id));
    const student = students?.[String(id)];
    const rawStatus = statusesByStudentId[String(id)] || {};
    const status = normalizeStatus(rawStatus);
    const source = student || enrollment;
    const payment = status.paymentOption || readEnrollmentPayment(source);
    const hasContractStatus = status.contractSigned !== null;
    const localImage = firstPresent(rawStatus.imageAuthorization, rawStatus.image_authorization, rawStatus.autorizaDireitoImagem);
    const imageAuthorization = localImage !== undefined ? status.imageAuthorization : readImageAuthorization(source);
    const hasLocalRestriction = ["imageRestrictions", "image_restrictions", "restricoesDireitoImagem"].some(key => rawStatus[key] !== undefined && rawStatus[key] !== null);
    const imageRestrictions = hasLocalRestriction ? status.imageRestrictions : readImageRestrictions(source);
    const hasAnyLoadedData = Boolean(status.id || student || source || payment || imageAuthorization || imageRestrictions);
    return [{ id: String(id), name: scalar(enrollment.Aluno || enrollment.AlunoNome || student?.Nome || "Nome não informado"),
      payment,
      label: hasAnyLoadedData ? PAYMENT_LABELS[payment] || "Não informado" : "Dados não carregados",
      contractSigned: hasContractStatus ? status.contractSigned : false,
      hasContractStatus,
      contractLabel: hasContractStatus ? CONTRACT_SIGNED_LABELS[String(status.contractSigned)] : "Não informado",
      imageAuthorization,
      imageAuthorizationLabel: IMAGE_AUTHORIZATION_LABELS[imageAuthorization] || "Não informado",
      imageRestrictions,
      imageRestrictionsLabel: imageRestrictions ? "Com restrição" : "Sem restrição" }];
  }).sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
}
